import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack, { type StackProps } from '@mui/material/Stack';
import Grid2 from '@mui/material/Grid2';
import { type ReactNode, Suspense, lazy, memo, useMemo } from 'react';
import { type CarDataFields } from '../../_configs/types';
import sStrDatafields from '../../_json/sStrDatafields';
import { type ConfigInfoGridPrefixes } from '../../_tools/utils';
import { type Car } from '../../_types/Car';
import { useSettingsContext } from '../../context/settings';
import useLocales from '../../hooks/useLocales';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import BigDataItemSkeleton from './big-data-item/skeleton';
import ListDataItemSkeleton from './list-data-item/skeleton';
import SmallDataItemSkeleton from './small-data-item/skeleton';

const BigDataItem = lazy(() => import('./big-data-item/component' /* webpackChunkName: "BigDataItem" */));
const ListDataItem = lazy(() => import('./list-data-item/component' /* webpackChunkName: "ListDataItem" */));
const SmallDataItem = lazy(() => import('./small-data-item/component' /* webpackChunkName: "SmallDataItem" */));

type CarDetailsGridProps = {
    bigFields?: ConfigInfoGridPrefixes<CarDataFields>[];
    car: Car<false>;
    colsBig: { xs: number; sm?: number; md?: number; lg?: number; xl?: number };
    colsList: { xs: number; sm?: number; md?: number; lg?: number; xl?: number };
    dividerMargin?: number;
    isCarDetailView?: boolean;
    listFields?: ConfigInfoGridPrefixes<CarDataFields>[];
    mini?: boolean;
    smallFields?: ConfigInfoGridPrefixes<CarDataFields>[];
    showCarVarbrauchDetailsButton?: ReactNode;
    sx?: StackProps['sx'];
};

function CarDetailsGrid(props: CarDetailsGridProps) {
    const { bigFields, car, colsBig, colsList, dividerMargin, isCarDetailView, listFields, mini, smallFields, showCarVarbrauchDetailsButton, sx } = props;
    const settings = useSettingsContext();
    const { translate } = useLocales();
    const plugInHybrid = car?.engine?.motor === 'Plug-In-Hybrid';
    const dataFields = useMemo(
        () => sStrDatafields(translate, settings?.general?.language, settings?.style?.fontSize, showCarVarbrauchDetailsButton),
        [translate, settings?.general?.language, settings?.style?.fontSize, showCarVarbrauchDetailsButton]
    );

    return (
        <Stack gap={1.5} divider={<Divider orientation="horizontal" flexItem sx={{ borderStyle: 'dashed', mx: dividerMargin }} />} sx={sx}>
            {(!isCarDetailView ? !checkUndefinedOrNull(bigFields) : !checkUndefinedNullOrEmpty(bigFields)) && (
                <Box sx={{ [getStyleQuery()]: { flexShrink: 0 } }}>
                    <Box sx={{ [getStyleQuery()]: { mx: -1, my: -1.5, overflow: 'hidden' } }}>
                        <Grid2
                            container
                            data-testid="CarDetailsGrid_Container"
                            spacing={0}
                            sx={
                                isCarDetailView
                                    ? {
                                        [getStyleQuery()]: {
                                            m: -0.5,
                                            '--Grid-borderWidth': '1px',
                                            'borderTop': 'var(--Grid-borderWidth) dashed',
                                            'borderLeft': 'var(--Grid-borderWidth) dashed',
                                            'borderColor': 'divider',
                                            '& > div': {
                                                borderRight: 'var(--Grid-borderWidth) dashed',
                                                borderBottom: 'var(--Grid-borderWidth) dashed',
                                                borderColor: 'divider',
                                            },
                                        },
                                    }
                                    : { py: 1 }
                            }
                        >
                            {bigFields?.map((item, itemIndex) => {
                                const clearKey = item?.replace('[G]', '')?.replace('[L]', '');
                                let keyToShow = clearKey === 'siegel_garantie' ? (!checkUndefinedNullOrEmpty(car?.siegel) && car?.siegel !== 0 ? 'siegel' : 'garantie') : clearKey;

                                if (plugInHybrid && (keyToShow === 'verbrauch.combined' || keyToShow === 'verbrauch.emission' || keyToShow === 'verbrauch.co2class')) {
                                    keyToShow = `${keyToShow}Weighted`;
                                }

                                const itemToShow = dataFields?.find((d) => d?.i === keyToShow);

                                return (
                                    <Grid2
                                        key={`bigFieldItem_${itemIndex}_${keyToShow}`}
                                        component={Stack}
                                        sx={{ [getStyleQuery()]: isCarDetailView ? { p: 1 } : { py: 0.5, px: 0.5 } }}
                                        size={{
                                            xs: itemToShow?.fullWidth ? 12 : !checkUndefinedNullOrEmpty(colsBig?.xs) ? 12 / colsBig?.xs : 12,
                                            sm: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsBig?.sm) ? 12 / colsBig?.sm : undefined,
                                            md: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsBig?.md) ? 12 / colsBig?.md : undefined,
                                            lg: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsBig?.lg) ? 12 / colsBig?.lg : undefined,
                                            xl: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsBig?.xl) ? 12 / colsBig?.xl : undefined,
                                        }}
                                    >
                                        <Suspense fallback={<BigDataItemSkeleton isCarDetailView={isCarDetailView} />}>
                                            <BigDataItem car={car} hideIcon={!isCarDetailView} isCarDetailView={isCarDetailView} itemToShow={itemToShow} />
                                        </Suspense>
                                    </Grid2>
                                );
                            })}
                        </Grid2>
                    </Box>
                </Box>
            )}
            {(!isCarDetailView ? !checkUndefinedOrNull(smallFields) : !checkUndefinedNullOrEmpty(smallFields)) && (
                <Box sx={{ [getStyleQuery()]: { flexGrow: 1 } }}>
                    <Box sx={{ [getStyleQuery()]: { mx: -0.5, overflow: 'hidden' } }}>
                        <Stack data-testid="CarDetailsGrid_ContainerGridVerbrauch" columnGap={0.25} rowGap={0.25} direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center">
                            {smallFields?.map((item, itemIndex) => {
                                const clearKey = item?.replace('[G]', '')?.replace('[L]', '');
                                let keyToShow = clearKey === 'siegel_garantie' ? (!checkUndefinedNullOrEmpty(car?.siegel) && car?.siegel !== 0 ? 'siegel' : 'garantie') : clearKey;

                                if (plugInHybrid && (keyToShow === 'verbrauch.combined' || keyToShow === 'verbrauch.emission' || keyToShow === 'verbrauch.co2class')) {
                                    keyToShow = `${keyToShow}Weighted`;
                                }

                                const itemToShow = item === 'spacer' ? item : dataFields?.find((d) => d?.i === keyToShow);

                                return (
                                    <Suspense key={`smallFieldItem_${itemIndex}_${keyToShow}`} fallback={<SmallDataItemSkeleton isCarDetailView={isCarDetailView} />}>
                                        <SmallDataItem car={car} hideIcon={!mini && !isCarDetailView} isCarDetailView={isCarDetailView} itemToShow={itemToShow} />
                                    </Suspense>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            )}
            {(!isCarDetailView ? !checkUndefinedOrNull(listFields) : !checkUndefinedNullOrEmpty(listFields)) && (
                <Box sx={{ [getStyleQuery()]: { flexGrow: 1 } }}>
                    <Box sx={{ [getStyleQuery()]: { mx: -1.5, my: -0.5, overflow: 'hidden' } }}>
                        <Grid2 container columnSpacing={0.25}>
                            {listFields?.map((item, itemIndex) => {
                                const clearKey = item?.replace('[G]', '')?.replace('[L]', '');
                                let keyToShow = clearKey === 'siegel_garantie' ? (!checkUndefinedNullOrEmpty(car?.siegel) && car?.siegel !== 0 ? 'siegel' : 'garantie') : clearKey;

                                if (plugInHybrid && (keyToShow === 'verbrauch.combined' || keyToShow === 'verbrauch.emission' || keyToShow === 'verbrauch.co2class')) {
                                    keyToShow = `${keyToShow}Weighted`;
                                }

                                const itemToShow = dataFields?.find((d) => d?.i === keyToShow);

                                return (
                                    <Grid2
                                        key={`listFieldItem_${itemIndex}_${keyToShow}`}
                                        component={Stack}
                                        size={{
                                            xs: itemToShow?.fullWidth ? 12 : !checkUndefinedNullOrEmpty(colsList?.xs) ? 12 / colsList?.xs : 12,
                                            sm: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsList?.sm) ? 12 / colsList?.sm : undefined,
                                            md: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsList?.md) ? 12 / colsList?.md : undefined,
                                            lg: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsList?.lg) ? 12 / colsList?.lg : undefined,
                                            xl: !itemToShow?.fullWidth && !checkUndefinedNullOrEmpty(colsList?.xl) ? 12 / colsList?.xl : undefined,
                                        }}
                                        sx={{ [getStyleQuery()]: { px: 1, py: 0.5 } }}
                                    >
                                        <Suspense fallback={<ListDataItemSkeleton />}>
                                            <ListDataItem car={car} hideIcon={!isCarDetailView} itemToShow={itemToShow} />
                                        </Suspense>
                                    </Grid2>
                                );
                            })}
                        </Grid2>
                    </Box>
                </Box>
            )}
        </Stack>
    );
}

export default memo(CarDetailsGrid);
